import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import BeforeAfterGallery from './components/BeforeAfterGallery';
import Vision from './components/Vision';
import Market from './components/Market';
import Features from './components/Features';
import Services from './components/Services';
import About from './components/About';
import PricingFactors from './components/PricingFactors';
import SOP from './components/SOP';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import ServiceArea from './components/ServiceArea';
import Roadmap from './components/Roadmap';
import CTA from './components/CTA';
import Footer from './components/Footer';

export interface SimulatorQuoteData {
  serviceType: 'grave' | 'garden';
  graveCount: number;
  graveLabel: string;
  areaSize: number;
  areaExtra: number;
  coreItems: Array<{ title: string; price: number }>;
  extraItems: Array<{ title: string; price: number }>;
  total: number;
}

export default function Home() {
  const [simulatorQuote, setSimulatorQuote] = useState<SimulatorQuoteData | null>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": `${siteUrl}/#localbusiness`,
          "name": "홈&그레이브",
          "alternateName": "Home & Grave",
          "description": "경남 진주 지역 전원주택 정원관리, 제초, 계절별 유지보수, 산소 벌초 대행 전문 서비스. GPS 면적 측정, 드론 촬영 보고, 21개 투명 요인 견적 제공.",
          "url": siteUrl,
          "telephone": "010-9879-8375",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "문산읍",
            "addressLocality": "진주시",
            "addressRegion": "경상남도",
            "postalCode": "52828",
            "addressCountry": "KR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "35.1796",
            "longitude": "128.1076"
          },
          "hasMap": "https://maps.google.com/?q=35.1796,128.1076",
          "areaServed": [
            { "@type": "City", "name": "진주시" },
            { "@type": "State", "name": "경상남도" }
          ],
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
              "opens": "08:00",
              "closes": "18:00"
            }
          ],
          "priceRange": "₩₩",
          "image": `${siteUrl}/og-image.jpg`,
          "sameAs": []
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "홈&그레이브",
          "description": "경남 진주 전원주택 정원관리·산소 벌초 대행 전문",
          "inLanguage": "ko-KR",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteUrl}/?s={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/#webpage`,
          "url": siteUrl,
          "name": "진주 전원주택 정원관리·산소 벌초 전문 홈&그레이브",
          "isPartOf": { "@id": `${siteUrl}/#website` },
          "about": { "@id": `${siteUrl}/#localbusiness` },
          "description": "경남 진주 전원주택 정원관리·산소 벌초 대행 전문. GPS 면적 측정, 드론 촬영 보고, 21개 투명 요인 견적 제공.",
          "inLanguage": "ko-KR",
          "breadcrumb": { "@id": `${siteUrl}/#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${siteUrl}/#breadcrumb`,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "홈",
              "item": siteUrl
            }
          ]
        },
        {
          "@type": "Service",
          "name": "산소 벌초 대행",
          "provider": { "@id": `${siteUrl}/#localbusiness` },
          "areaServed": { "@type": "City", "name": "진주시" },
          "description": "경남 진주 지역 산소 벌초 대행 서비스. GPS 면적 측정 및 드론 촬영 보고 제공.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "KRW",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          "name": "전원주택 정원관리",
          "provider": { "@id": `${siteUrl}/#localbusiness` },
          "areaServed": { "@type": "City", "name": "진주시" },
          "description": "경남 진주 전원주택 정원 관리, 제초, 계절별 유지보수 서비스.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "KRW",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "진주 벌초 서비스 비용은 어떻게 산출되나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "GPS 기반 정밀 면적 측정과 경사도, 접근성, 잡초 밀도, 이동 거리 등 21개 현장 요인을 종합 분석해 견적을 산출합니다. 기본 산소 벌초는 100,000원부터 시작하며, 4기 이상 동일 구역 묶음 작업 시 300,000원 정액 할인 패키지를 적용해 드립니다. 무료 견적 신청을 통해 정확한 금액을 먼저 확인하실 수 있습니다."
              }
            },
            {
              "@type": "Question",
              "name": "전원주택 정원관리 연간 구독 비용은 어떻게 되나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "스탠다드 플랜은 연 120만 원부터 시작하며 분기별 정기 방문(연 4회), 전지·예초·주택 노후 점검, 포토 리포트 전송이 포함됩니다. 프리미엄 플랜은 연 240만 원부터로 격월 정밀 관리(연 6회), 병해충 방제, 시설물 소수리, 긴급 출동 서비스, 드론 촬영 리포트까지 제공됩니다. 비구독 시 연간 예상 비용 380만 원 대비 최대 68% 절감 효과가 있습니다."
              }
            },
            {
              "@type": "Question",
              "name": "서비스 가능 지역은 어디까지인가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "경남 진주시 전 지역을 기본 서비스 권역으로 운영합니다. 특히 문산읍, 금산면, 지수면, 집현면, 대곡면 등 진주 외곽 전원주택 밀집 지역과 선산이 많은 농촌 지역을 중점 커버합니다. 인근 사천시, 고성군, 함안군 등도 현장 여건에 따라 출장 가능하니 별도 문의 부탁드립니다."
              }
            },
            {
              "@type": "Question",
              "name": "작업 후 결과를 어떻게 확인할 수 있나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "작업 완료 즉시 드론으로 촬영한 전·후 비교 사진과 전문가 소견서를 SMS로 전송해 드립니다. 드론 촬영 보고서에는 작업 면적, 제거된 식생 유형, 시설물 상태 점검 결과가 포함되며, 선산의 경우 봉분·비석·석축 상태까지 기록됩니다. 원격으로도 언제 어디서나 작업 결과를 확인하실 수 있습니다."
              }
            },
            {
              "@type": "Question",
              "name": "작업 일정은 어떻게 조율하나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "견적 확정 후 고객이 희망하는 날짜에 맞춰 일정을 조율합니다. 성묘 전날·추석 전 벌초의 경우 최소 2주 전 예약을 권장합니다. 작업 당일 기상 악화(폭우·태풍 경보) 시 자동 일정 재조율 알림 SMS를 발송하며, 완료 후 결과 보고까지 같은 날 처리됩니다."
              }
            },
            {
              "@type": "Question",
              "name": "10년 위성 데이터 분석이란 무엇인가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "10년치 위성 이미지 데이터를 분석해 해당 부지의 식생 변화, 침식 위험 구간, 계절별 잡초 성장 패턴을 파악합니다. 이 데이터를 토대로 GPS 측정 전 현장 예비 분석이 가능해 초기 견적 정확도가 높아지고, 향후 관리 계획도 선제적으로 수립할 수 있습니다. 전원주택 정원과 산소 선산 모두에 동일하게 적용됩니다."
              }
            },
            {
              "@type": "Question",
              "name": "GPS 면적 측정은 얼마나 정확한가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "RTK-GPS 정밀 측량 방식을 적용해 ±3% 이내의 오차 범위로 면적을 산출합니다. 기존 육안·줄자 측정 대비 최대 15% 오차를 줄여 과도한 견적 청구를 방지합니다. 측정 결과는 고객에게 수치로 공유되며, 견적서에 면적 근거가 명시됩니다."
              }
            },
            {
              "@type": "Question",
              "name": "산소 벌초 외에 묘지 보수 작업도 가능한가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "네, 전문 석축·보수 서비스를 별도로 제공합니다. 봉분 정비, 비석 세척, 석축 유실 복원, 배수로 정비 등 고난도 공사는 평당 230,000원부터 시작합니다. 벌초 작업 중 시설물 이상이 발견되면 현장에서 즉시 사진과 함께 상태를 보고드리고, 추가 작업 진행 여부를 먼저 확인 후 시공합니다."
              }
            },
            {
              "@type": "Question",
              "name": "전원주택 정원관리에는 어떤 작업이 포함되나요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "정밀 예초(잔디 최적 높이 조절), 수종별 전지(수형 관리·가지치기), 잡초 제거, 병해충 방제, 시비 작업, 목재 데크 케어, 석축·법면 점검, 배수로 소통, 포토 리포트 전송까지 토탈 케어를 제공합니다. 수종 선별 및 식재 추천도 별도 상담으로 가능하며, 아름다운 정원 데이터 기반 수종 매칭 서비스도 제공합니다."
              }
            },
            {
              "@type": "Question",
              "name": "연간 구독은 중간에 해지 가능한가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "네, 위약금 없이 언제든 해지 신청이 가능합니다. 이미 제공된 회차의 서비스 금액을 정산 후 남은 금액을 환불해 드립니다. 일시적 사정으로 특정 회차 방문을 다음 달로 미루는 일정 조율도 가능하니 부담 없이 말씀해 주세요."
              }
            },
            {
              "@type": "Question",
              "name": "첫 방문 전 현장 실사가 필요한가요?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "위성 데이터와 고객 제공 정보로 기본 견적을 먼저 산출한 뒤, 현장 방문 실사를 통해 최종 견적을 확정합니다. 초회 현장 실사는 무료이며, 이 과정에서 GPS 면적 측정·드론 예비 촬영·토양 상태 확인이 이루어집니다. 실사 후 24시간 이내에 상세 견적서를 문자·카카오톡으로 전달해 드립니다."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-home');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Hero onSimulatorQuote={setSimulatorQuote} />
      <Vision />
      <Market />
      <Features />
      <SOP />
      <BeforeAfterGallery />
      <Services />
      <About />
      <PricingFactors onSimulatorQuote={setSimulatorQuote} />
      <Testimonials />
      <FAQ />
      <ServiceArea />
      <Roadmap />
      <CTA simulatorQuote={simulatorQuote} onClearSimulatorQuote={() => setSimulatorQuote(null)} />
      <Footer />
    </div>
  );
}